// Third-party
import Head from 'next/head';

// First-party
import { Slot, createPage } from '@lib/util/templateHelpers';
import { gameToLdJson } from '@lib/util/gameToLdJson';
import Meta from '@lib/components/head/Meta';


// Component lib
import shared from '@lib/components';

// Local
import gameApi from '@/apis/drupal/game';
import GameTileLayout from '@/layouts/GameTileLayout';

// Local components
import AllGamesBlock from '@/components/AllGamesBlock';

const {Box, Block, Title, RouterLink, DocumentTitle, Content} = shared;

export async function getServerSideProps () {
  const games = await gameApi.listNew(process.env.APP_CONFIG.DEFAULT_GAME_LIMIT);
  
  return {
    props: {
      games
    },
  };
}

export default createPage('NewPage', {}, function NewPage ({ className }, props) {
	const title = 'New IO Games';
	const description = 'Discover the latest io games on iogames.space. Explore the newest io games that have caught our attention, including recent hits like Baseball Bros, Hexanaut, and Deadshot.';
  return (
    <GameTileLayout className={className}>
      <DocumentTitle title='New IO Games' />

      <Meta
				title={title}
				description={description}
				openGraph={{
				title: title,
				description: description,
				type: 'website'
				}}
			>
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'WebSite',
              'name': process.env.APP_CONFIG.DEFAULT_TITLE,
              'url': process.env.APP_CONFIG.URL + '/new'
            }, null, 2)
          }}
        />
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Thing", 
                    "@id": process.env.APP_CONFIG.URL,
                    "name": "Games"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Thing", 
                    "@id":process.env.APP_CONFIG.URL + '/new',
                    "name": "New io Games"
                  }
                }
              ],
            }, null, 2)
          }}
        />

        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@type': 'ItemList',
              'itemListElement': props.games.map((game, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'url': process.env.APP_CONFIG.URL + game.path,
                'name': game.name
               // 'item': gameToLdJson(game)
              }))
            }, null, 2)
          }}
        />
      </Meta>

      <AllGamesBlock feature games={props.games} fetchMoreGames={gameApi.listNew}>
        <Slot name='title'>New io Games</Slot>
      </AllGamesBlock>
      {
        <Block>
          <Box className='IndexPage__BottomBox'>
            <Title>New IO Games</Title>
            <Content>
              <Title h2 size4>Discover the Latest Additions to the IO Gaming World</Title>

              <p>Welcome to our New Games section, where we showcase the freshest io games just released into the wild! At <RouterLink href="/">iogames.space</RouterLink>, we're constantly searching for creative titles that redefine browser entertainment. Here you'll find the newest io games that have recently captured player interest, including standout additions like <RouterLink href="/baseball-bros">Baseball Bros</RouterLink>, <RouterLink href="/hexanautio">Hexanaut</RouterLink>, and <RouterLink href="/deadshot-io">Deadshot</RouterLink>.</p>

              <Title h2 size4>Why Try New IO Games?</Title>
              <p>New io games often introduce inventive mechanics, visual styles, and social features that keep online gaming exciting. Exploring the latest releases gives players the opportunity to stay ahead of trends while discovering experiences that feel fresh and original.</p>
              <ul>
                <li>Test innovative gameplay concepts before they become widely popular</li>
                <li>Enjoy modern browser performance and smoother multiplayer action</li>
                <li>Support developers experimenting with unique game ideas</li>
                <li>Join growing communities from the very beginning</li>
                <li>Challenge yourself with completely new strategies and mechanics</li>
              </ul>

              <Title h2 size4>Our Selection Process</Title>
              <p>Every new title featured on our platform goes through a careful review process. We look for originality, responsive gameplay, strong replay value, and active player engagement before highlighting a game in this section. Competitive titles with fast reaction-based mechanics often stand out, especially genres similar to modern <a className='RouterLink' href="https://iogames.space/en/crash-gambling-sites">crash betting games</a>, where timing and momentum create intense moments of excitement.</p>

              <Title h2 size4>Recently Added Online Games</Title>
              <p>Recent additions to our library continue to showcase the diversity of browser gaming. <RouterLink href="/baseball-bros">Baseball Bros</RouterLink> brings energetic sports competition into short multiplayer matches, while <RouterLink href="/deadshot-io">Deadshot</RouterLink> rewards players with sharp aim and tactical positioning. Meanwhile, <RouterLink href="/hexanautio">Hexanaut</RouterLink> offers a more strategic pace focused on territory expansion and map control. <a className='RouterLink' href="https://iogames.space/slots/a-night-with-cleo">A Night With Cleo online slot</a> stands out as another recent highlight with its Egyptian-inspired design and engaging gameplay.</p>
              <p>Alongside competitive multiplayer titles, many players also enjoy exploring immersive <a className='RouterLink' href="https://iogames.space/category/casino">casino games</a> that deliver quick and engaging gameplay sessions directly in the browser.</p>
              <p>We constantly refresh this page with promising new releases, making it the perfect place to discover upcoming favorites before they become mainstream hits. Found an exciting new io title we should feature? <RouterLink href="/contact-us">Reach out to our team</RouterLink> and share your recommendation!</p>
            </Content>
          </Box>
        </Block>
      }
    </GameTileLayout>
  );
});
